import type { LucideIcon } from 'lucide-react'
import { BarChart3, Clapperboard, Film, Lightbulb, Replace, Search, Sparkles, Target, Zap } from 'lucide-react'

export const MARKETPLACE_ROLE_NAMES = [
  'Ad Creative Strategist',
  'Brand Creative Analyst',
  'Competitive Creative Analyst',
  'Copywriter',
  'Creative Research Specialist',
  'Creator Performance Analyst',
  'Video Asset Tagger',
] as const

/**
 * Fallback agent IDs per role — mirrors the pattern used in creative-analyzer.ts and ad-analyzer.ts.
 * Used when listMarketAgents / listAgents returns no results for a role.
 * All roles fall back to the shared general-purpose marketing agent.
 */
export const FALLBACK_AGENT_ID_BY_ROLE: Partial<Record<string, string>> = {
  'Ad Creative Strategist':       '1769630536650047000-513506',
  'Brand Creative Analyst':       '1769630536650047000-513506',
  'Competitive Creative Analyst': '1769630536650047000-513506',
  'Copywriter':                   '1769630536650047000-513506',
  'Creative Research Specialist': '1769630536650047000-513506',
  'Creator Performance Analyst':  '1769630536650047000-513506',
  'Video Asset Tagger':           '1769630536650047000-513506',
}

export const DEFAULT_FALLBACK_AGENT_ID = '1769630536650047000-513506'

export interface MarketingChatSuggestion {
  icon: LucideIcon
  text: string
}

export interface MarketingChatAction {
  icon: LucideIcon
  colorClass: string
  title: string
  desc: string
  prompt: string
}

export interface AgentPlanContext {
  id: string
  name: string
  feature: string
  source: 'nextstore'
  version: string
}

export interface MarketingChatPreset {
  id: string
  title: string
  welcomeTitle: string
  welcomeDescription: string
  roles: readonly string[]
  defaultRoleName: string
  suggestions: MarketingChatSuggestion[]
  quickActions: MarketingChatAction[]
  inputPlaceholder: string
  disclaimer: string
  agentPlan: AgentPlanContext
  systemPrompt?: string
}

const CURRENT_PLAN_VERSION = '2026-02-05'

export const DEFAULT_MARKETING_CHAT_PRESET: MarketingChatPreset = {
  id: 'marketing-ai-chats',
  title: 'Marketing AI Chats',
  welcomeTitle: 'Welcome to Marketing Chats',
  welcomeDescription:
    'Pick a role and start a conversation. I can help with strategy, insights, creative ideas, and copy.',
  roles: MARKETPLACE_ROLE_NAMES,
  defaultRoleName: 'Ad Creative Strategist',
  quickActions: [
    {
      icon: Sparkles,
      colorClass: 'text-blue-600',
      title: 'Strategy',
      desc: 'Get a campaign strategy outline',
      prompt: 'I need help creating a campaign strategy.',
    },
    {
      icon: Sparkles,
      colorClass: 'text-purple-600',
      title: 'Insights',
      desc: 'Extract trends and learnings',
      prompt: 'Extract key insights and trends from recent ads.',
    },
    {
      icon: Sparkles,
      colorClass: 'text-green-600',
      title: 'Ideas',
      desc: 'Brainstorm creative concepts',
      prompt: 'Brainstorm 10 creative concepts for this product.',
    },
    {
      icon: Sparkles,
      colorClass: 'text-orange-600',
      title: 'Copy',
      desc: 'Draft hooks and ad copy',
      prompt: 'Write 5 hooks and 3 ad copy variations.',
    },
  ],
  suggestions: [
    { icon: Lightbulb, text: 'Generate creative angles for my product' },
    { icon: Target, text: "Analyze a competitor's ads and summarize insights" },
    { icon: Zap, text: 'Improve performance for my current campaign' },
  ],
  inputPlaceholder: 'Ask anything about marketing...',
  disclaimer: 'Responses are streamed in real-time. Check important info.',
  agentPlan: {
    id: 'marketing-ai-chats',
    name: 'Marketing AI Chats',
    feature: 'marketing-chats',
    source: 'nextstore',
    version: CURRENT_PLAN_VERSION,
  },
}

export const AI_AD_CHIEF_PRESET: MarketingChatPreset = {
  id: 'ai-ad-chief',
  title: 'AI Ad Chief',
  welcomeTitle: 'Welcome to AI Ad Chief',
  welcomeDescription:
    'Build stronger ad concepts, hooks, and angles with your selected Ad Chief role.',
  roles: [
    'Ad Creative Strategist',
    'Brand Creative Analyst',
    'Competitive Creative Analyst',
  ],
  defaultRoleName: 'Ad Creative Strategist',
  quickActions: [
    {
      icon: Sparkles,
      colorClass: 'text-blue-600',
      title: 'Hook Copy',
      desc: 'Generate high-impact hooks',
      prompt: 'Create 10 hook-copy options for my next paid campaign.',
    },
    {
      icon: Sparkles,
      colorClass: 'text-purple-600',
      title: 'Hook Visual',
      desc: 'Design visual opening ideas',
      prompt: 'Propose 8 hook-visual concepts for video ads.',
    },
    {
      icon: Sparkles,
      colorClass: 'text-green-600',
      title: 'Ad Structure',
      desc: 'Build ad narrative flow',
      prompt: 'Give me a complete ad structure from hook to CTA.',
    },
    {
      icon: Sparkles,
      colorClass: 'text-orange-600',
      title: 'Angles',
      desc: 'Brainstorm persuasive angles',
      prompt: 'Brainstorm 12 angle ideas grouped by customer pain points.',
    },
  ],
  suggestions: [
    { icon: Lightbulb, text: 'Create hook options for a new ad concept' },
    { icon: Target, text: 'Compare my current ad angle vs competitors' },
    { icon: Zap, text: 'Rewrite this ad to improve click-through rate' },
  ],
  inputPlaceholder: 'Ask AI Ad Chief...',
  disclaimer: 'AI Ad Chief can make mistakes. Check important info.',
  agentPlan: {
    id: 'ai-ad-chief',
    name: 'AI Ad Chief',
    feature: 'ai-ad-chief',
    source: 'nextstore',
    version: CURRENT_PLAN_VERSION,
  },
}

export const AI_COPY_CHIEF_PRESET: MarketingChatPreset = {
  id: 'ai-copy-chief',
  title: 'AI Copy Chief',
  welcomeTitle: 'Welcome to AI Copy Chief',
  welcomeDescription:
    'Generate persuasive copy, improve existing messages, and discover better positioning ideas.',
  roles: ['Copywriter', 'Creative Research Specialist', 'Ad Creative Strategist'],
  defaultRoleName: 'Copywriter',
  quickActions: [
    {
      icon: Sparkles,
      colorClass: 'text-blue-600',
      title: 'Improve Copy',
      desc: 'Upgrade existing copy',
      prompt: 'Improve this copy for clarity, persuasion, and emotional pull.',
    },
    {
      icon: Sparkles,
      colorClass: 'text-purple-600',
      title: 'New Copy',
      desc: 'Create fresh ad copy',
      prompt: 'Create 5 high-converting ad copy variants for my product.',
    },
    {
      icon: Sparkles,
      colorClass: 'text-green-600',
      title: 'Persona',
      desc: 'Deep dive buyer motivations',
      prompt: 'Build a deep buyer persona and explain why they would purchase.',
    },
    {
      icon: Sparkles,
      colorClass: 'text-orange-600',
      title: 'Landing Copy',
      desc: 'Draft landing-page sections',
      prompt: 'Write hero, benefits, and CTA sections for a landing page.',
    },
  ],
  suggestions: [
    { icon: Lightbulb, text: 'Rewrite this headline with more urgency' },
    { icon: Target, text: 'Create copy for a cold audience campaign' },
    { icon: Zap, text: 'Give me 20 short ad hooks for this offer' },
  ],
  inputPlaceholder: 'Ask AI Copy Chief...',
  disclaimer: 'AI Copy Chief can make mistakes. Check important info.',
  agentPlan: {
    id: 'ai-copy-chief',
    name: 'AI Copy Chief',
    feature: 'ai-copy-chief',
    source: 'nextstore',
    version: CURRENT_PLAN_VERSION,
  },
}

export const AI_AD_ANALYZER_PRESET: MarketingChatPreset = {
  id: 'ai-ad-analyzer',
  title: 'AI Ad Analyzer',
  welcomeTitle: 'Welcome to AI Ad Analyzer',
  welcomeDescription:
    'Analyze creatives, diagnose weak points, and get prioritized recommendations before launch.',
  roles: [
    'Brand Creative Analyst',
    'Competitive Creative Analyst',
    'Creator Performance Analyst',
    'Video Asset Tagger',
  ],
  defaultRoleName: 'Brand Creative Analyst',
  quickActions: [
    {
      icon: Search,
      colorClass: 'text-blue-600',
      title: 'Audit',
      desc: 'Run a creative quality audit',
      prompt: 'Audit this ad concept and rate messaging, structure, and clarity.',
    },
    {
      icon: BarChart3,
      colorClass: 'text-purple-600',
      title: 'Performance',
      desc: 'Find performance blockers',
      prompt: 'List the top 5 reasons this ad might underperform and how to fix each.',
    },
    {
      icon: Target,
      colorClass: 'text-green-600',
      title: 'Competitors',
      desc: 'Benchmark vs market',
      prompt: 'Compare this ad concept against strong competitor patterns.',
    },
    {
      icon: Sparkles,
      colorClass: 'text-orange-600',
      title: 'Next Iteration',
      desc: 'Generate actionable improvements',
      prompt: 'Propose a higher-converting next iteration with concrete copy and visual changes.',
    },
  ],
  suggestions: [
    { icon: Lightbulb, text: 'What are the weakest parts of this ad concept?' },
    { icon: Target, text: 'How can I improve the first 3 seconds of this ad?' },
    { icon: Zap, text: 'Give me a prioritized optimization checklist' },
  ],
  inputPlaceholder: 'Ask AI Ad Analyzer...',
  disclaimer: 'AI Ad Analyzer can make mistakes. Check important info.',
  agentPlan: {
    id: 'ai-ad-analyzer',
    name: 'AI Ad Analyzer',
    feature: 'ai-ad-analyzer',
    source: 'nextstore',
    version: CURRENT_PLAN_VERSION,
  },
}

const STORYBOARD_REPLACEMENT_SYSTEM_PROMPT = `You are the TrafiFlow Storyboard Replacement Engine.

Your role is to analyze an existing ad storyboard and generate:
- Scene-by-scene clip replacement suggestions
- AI-generated search tags for repository matching
- Footage ranking criteria
- Crop/trim recommendations for each clip
- Optional hook variations (first 3-5 seconds only)
- Structured output formatted for UI rendering

You must reason visually and structurally, not generically.

## CONTEXT YOU WILL RECEIVE

You will receive:
- Brand parameters
- Persona description
- Script (scene segmented with timestamps)
- Current footage metadata (if available)
- Repository asset metadata (IDs, tags, duration, POV, model type, shot size, etc.)

You MUST use brand alignment as a strict filter.

## PHASE 1 — AI FOOTAGE SUGGESTION ENGINE

### STEP 1.1 — Scene Intent Extraction

For each scene, identify and output:
- Scene ID
- Timestamp Range
- Funnel Role (Hook / Authority / Pain / Agitate / Solution / Benefit / CTA)
- Emotional Intensity (Low / Medium / High)
- Visual Requirement Type: Talking Head, Product Demo, Lifestyle Context, Split Screen, Before/After, Close-up Detail, Animated Overlay
- Required Model Type: Inclusive / Curvy / Athletic / Mature / Young / Any
- Required Movement (if applicable): Bend, Sit, Strap Pull, Close-up Texture, Side angle, Front angle, Over-the-shoulder
- Tone Constraint: Natural UGC, Studio Clean, Educational, Emotional, Problem-Focused

Then generate AI Search Tags (8-15 per scene). Tags must be visual, physical, action-based — never abstract.
Example tags:
- "ugc woman adjusting bra strap"
- "close up wire free bra texture"
- "plus size woman bending in shapewear"
- "natural lighting bedroom testimonial"
- "side spillage demo mirror selfie"

### STEP 1.2 — Repository Matching Logic

Prioritize matches in this order:
1. Brand alignment score
2. Model alignment
3. Emotional tone match
4. Shot size match
5. Movement/action match
6. Duration compatibility (must allow trim to fit timestamp)
7. Freshness (avoid repetition)

Reject assets if: overly glam, hyper sexualized, mismatched body standard, wrong funnel stage tone, conflicts with script emotional arc.

### STEP 1.3 — Asset Recommendation Output

For each scene return:
- Suggested Primary Asset: Asset ID, Match Confidence (0-100), Reason for match (max 2 sentences)
- Trim Recommendation: Suggested in-point, out-point, reasoning. If first 3s are weak, suggest alternative micro-cut.
- Secondary Backup Option: Asset ID, why it's second choice

## PHASE 2 — CREATIVE ASSEMBLY OUTPUT

Return structured scene objects:
{
  scene_id, timestamp, funnel_stage, copy, visual_strategy,
  ai_tags: [],
  primary_asset: { asset_id, confidence, trim: { start_second, end_second, reasoning } },
  backup_asset: { asset_id, reasoning },
  hook_variations: []
}

### Hook Testing Mode
If user requests variation for first 3-5 seconds, ONLY regenerate Scene 1 with 3 hook types:
1. Pattern Interrupt
2. Relatable Pain
3. Authority / Bold Claim
Each must suggest different visual type, model framing, pacing, and specific trim suggestion.

## TRIM INTELLIGENCE

- Identify peak micro-expression and clean sentence boundary
- Avoid mid-word cuts
- Prefer movement midpoint (e.g., strap pull tension moment)
- If demo-based: trim at visual proof moment
- Clip longer than scene: suggest optimal 2-4 second window
- Clip shorter: flag as insufficient

## BRAND ALIGNMENT SCORING

Compute Brand Alignment Score (0-100) per scene based on: inclusivity match, tone consistency, emotional arc consistency, visual realism, funnel alignment.
If score < 70: provide correction note.

## HARD RULES

You MUST NOT: generate generic footage descriptions, suggest vague tags like "woman bra", ignore brand persona, overwrite script meaning, suggest impossible transitions, suggest mismatched emotional tone.

You MUST: think visually, think in cuts, think in pacing, think in Meta ad performance logic, keep replacement realistic to repository constraints.

## ADVANCED REASONING

Before generating output, internally reason about: Does the emotional arc escalate correctly? Does visual pacing match script pacing? Are we front-loading engagement? Is hook strong enough for Meta? Are benefits visually proven?
Do NOT output reasoning chain — only output structured result.

## Repository Gap Detection

If asset pool insufficient, return:
{ repository_gap_detected: true, required_asset_spec: { model_type, action, environment, shot_size, tone } }`

export const STORYBOARD_REPLACEMENT_PRESET: MarketingChatPreset = {
  id: 'storyboard-replacement-engine',
  title: 'Storyboard Replacement Engine',
  welcomeTitle: 'Welcome to the Storyboard Replacement Engine',
  welcomeDescription:
    'Analyze storyboards scene-by-scene and get AI-powered footage replacement suggestions, trim recommendations, and hook variations.',
  roles: [
    'Ad Creative Strategist',
    'Video Asset Tagger',
    'Brand Creative Analyst',
  ],
  defaultRoleName: 'Ad Creative Strategist',
  quickActions: [
    {
      icon: Replace,
      colorClass: 'text-blue-600',
      title: 'Scene Replacements',
      desc: 'Get clip suggestions per scene',
      prompt:
        'Analyze my storyboard and suggest replacement clips for each scene. Prioritize brand alignment, model match, and emotional tone.',
    },
    {
      icon: Film,
      colorClass: 'text-purple-600',
      title: 'Hook Variations',
      desc: 'Test 3 hook alternatives',
      prompt:
        'Generate 3 hook variations for the first 3-5 seconds of my storyboard: Pattern Interrupt, Relatable Pain, and Authority/Bold Claim. Include different visual type, model framing, and trim suggestions.',
    },
    {
      icon: Clapperboard,
      colorClass: 'text-green-600',
      title: 'Trim & Crop',
      desc: 'Optimize clip timing',
      prompt:
        'Review each scene in my storyboard and provide trim recommendations. Identify peak micro-expressions, clean sentence boundaries, and optimal cut points for each clip.',
    },
    {
      icon: Target,
      colorClass: 'text-orange-600',
      title: 'Brand Alignment',
      desc: 'Score footage vs brand',
      prompt:
        'Score each scene in my storyboard for brand alignment (0-100). Flag any clips below 70 with correction notes covering inclusivity, tone, emotional arc, and visual realism.',
    },
  ],
  suggestions: [
    { icon: Sparkles, text: 'Generate AI search tags for all scenes in my storyboard' },
    { icon: Zap, text: 'Find repository gaps and missing asset specs for my storyboard' },
    { icon: Lightbulb, text: 'Rebuild the full creative assembly with primary and backup assets' },
  ],
  inputPlaceholder: 'Paste your storyboard script or describe the scenes...',
  disclaimer: 'Storyboard Replacement Engine can make mistakes. Verify asset matches before export.',
  agentPlan: {
    id: 'storyboard-replacement-engine',
    name: 'Storyboard Replacement Engine',
    feature: 'storyboard-replacement',
    source: 'nextstore',
    version: CURRENT_PLAN_VERSION,
  },
  systemPrompt: STORYBOARD_REPLACEMENT_SYSTEM_PROMPT,
}
